<script>
  // Shared card tile: image stack, rounded card art, hover overlay.
  // The footer slot is caller-supplied so search and update feed can each
  // show their own metadata below the card without coupling.
  //
  // Props:
  //   imageUrl  {string|null}  — resolved image URL (caller uses getCardImageUrl)
  //   name      {string}       — card name for alt text / aria-label
  //   stackDepth {number}      — 1 = single card, 2 = two-card stack, 3+ = three-card stack
  //   disabled  {boolean}      — if true: dimmed + no hover lift (for "removed" cards)
  //   onclick   {function}     — click handler; if omitted the tile is not a button
  //   footer    {Snippet}      — Svelte snippet rendered below the card image
  //   overlay   {Snippet}      - Svelte snippet drawn over the card (e.g. the
  //                               shared CardPills), outside the image's clip so
  //                               a pill may straddle the card's edge
  /**
   * @type {{
   *   imageUrl?: string | null,
   *   name?: string,
   *   stackDepth?: number,
   *   disabled?: boolean,
   *   onclick?: ((e: MouseEvent) => void) | null,
   *   footer?: import('svelte').Snippet,
   *   overlay?: import('svelte').Snippet,
   * }}
   */
  let {
    imageUrl = null,
    name = '',
    stackDepth = 1,
    disabled = false,
    onclick = null,
    footer,
    overlay,
  } = $props();

  const isInteractive = $derived(!!onclick && !disabled);
</script>

<!-- One markup body for both the interactive (<button>) and static (<div>)
     branches; the hover-search overlay only renders for the interactive tile. -->
{#snippet stack()}
  <div class="card-image-stack">
    {#if stackDepth >= 3}
      <div class="stack-layer stack-3" aria-hidden="true"></div>
    {/if}
    {#if stackDepth >= 2}
      <div class="stack-layer stack-2" aria-hidden="true"></div>
    {/if}
    <div class="card-image-wrapper">
      {#if imageUrl}
        <img src={imageUrl} alt={name} loading="lazy" width="488" height="680" />
      {:else}
        <div class="card-placeholder" aria-hidden="true">
          <span class="card-placeholder-name">{name}</span>
        </div>
      {/if}
      {#if isInteractive}
        <div class="card-hover-overlay" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
      {/if}
    </div>
    {#if overlay}{@render overlay()}{/if}
  </div>
  {#if footer}
    <div class="card-tile-footer">
      {@render footer()}
    </div>
  {/if}
{/snippet}

{#if isInteractive}
  <button class="card-tile" class:card-tile--disabled={disabled} {onclick} aria-label="View {name}">
    {@render stack()}
  </button>
{:else}
  <!-- Non-interactive tile (no onclick, or disabled) -->
  <div class="card-tile card-tile--static" class:card-tile--disabled={disabled}>
    {@render stack()}
  </div>
{/if}

<style>
  /* ── Tile container ── */
  .card-tile {
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    transition: transform 0.2s ease;
  }

  .card-tile--static {
    cursor: default;
  }

  .card-tile:hover:not(.card-tile--static):not(.card-tile--disabled) {
    transform: translateY(-4px);
  }

  /* Removed / disabled: desaturate + dim */
  .card-tile--disabled {
    opacity: 0.55;
    filter: grayscale(0.6);
  }

  /* ── Stack effect ── */
  .card-image-stack {
    position: relative;
  }

  .stack-layer {
    position: absolute;
    border-radius: 0.5rem;
  }

  .stack-layer.stack-3 {
    top: 5px;
    left: 5px;
    right: -5px;
    bottom: -5px;
    background: var(--color-border, #e6e0d5);
  }

  .stack-layer.stack-2 {
    top: 2.5px;
    left: 2.5px;
    right: -2.5px;
    bottom: -2.5px;
    background: var(--color-accent-bg, #f0ebe3);
  }

  /* ── Card image ── */
  .card-image-wrapper {
    position: relative;
    border-radius: 0.5rem;
    overflow: hidden;
    /* Maintain card aspect ratio while image loads */
    aspect-ratio: 488 / 680;
    background: var(--color-bg-tray, #f3efe7);
  }

  .card-image-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 0.5rem;
  }

  /* ── Placeholder when no image URL ── */
  .card-placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
  }

  .card-placeholder-name {
    font-size: 0.7rem;
    color: var(--color-text-muted, #b5aa96);
    text-align: center;
    line-height: 1.3;
    font-weight: 500;
    word-break: break-word;
    hyphens: auto;
  }

  /* ── Hover overlay ── */
  .card-hover-overlay {
    position: absolute;
    inset: 0;
    background: rgba(44, 36, 25, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    opacity: 0;
    transition: opacity 0.2s ease;
    color: white;
    pointer-events: none;
  }

  .card-tile:hover:not(.card-tile--disabled) .card-hover-overlay {
    opacity: 1;
  }

  /* ── Footer area ── */
  .card-tile-footer {
    padding: 8px 2px 0;
  }

  /* ── Mobile adjustments ── */
  @media (max-width: 768px) {
    .card-tile:hover {
      transform: none;
    }

    .card-hover-overlay {
      display: none;
    }
  }
</style>
